import { useRef, useEffect, useMemo } from 'react';
import NudosSearchbarDropdown from 'components/NudosComponents/NudosSearchbarDropdown/NudosSearchbarDropdown';
import NudosHoverText from 'components/NudosComponents/NudosHoverText/NudosHoverText';
import NudosProductPills from 'components/NudosComponents/NudosProductPills/NudosProductPills';
import { IProductReference } from '../../../../../../../../../types/services';
import { IpillsByReference } from '../../../../../../../../../types/orders';
import { META_BASE_STOCK_ORDER_URL } from '../../../../../../../../../types/stockOrders';

import { bordColorTheme } from 'styles/styles.constants';
import { BordButton, BordIlustrationIcon, BordOneToneIcon } from 'bord-design-system';
import './ToolsSearchDropdown.scss';

interface IToolWithSelection extends IProductReference {
  isSelected: boolean;
}

interface IToolsSearchDropdownProps {
  searchQuery: string;
  availableTools: IProductReference[];
  selectedToolIds: Set<number>;
  onSearchQueryChange: (query: string) => void;
  onToolToggle: (tool: IProductReference) => void;
}

/**
 * Componente wrapper para el dropdown de búsqueda de herramientas con soporte para checkboxes
 * @param searchQuery - Término de búsqueda actual
 * @param availableTools - Lista de herramientas disponibles
 * @param selectedToolIds - Set de IDs de herramientas seleccionadas
 * @param onSearchQueryChange - Callback cuando cambia el término de búsqueda
 * @param onToolToggle - Callback cuando se selecciona/deselecciona una herramienta
 */
const ToolsSearchDropdown = ({
  searchQuery,
  availableTools,
  selectedToolIds,
  onSearchQueryChange,
  onToolToggle
}: IToolsSearchDropdownProps) => {
  const onToolToggleRef = useRef(onToolToggle);

  const currentValue = searchQuery ? { id: 0, name: searchQuery, sku: '', pills: {}, isSelected: false } : undefined;

  const toolsWithSelection = useMemo<IToolWithSelection[]>(() => {
    return availableTools.map(tool => ({
      ...tool,
      isSelected: selectedToolIds.has(tool.id)
    }));
  }, [availableTools, selectedToolIds]);

  const formDropdownOptions = (
    options: IToolWithSelection[],
    clickOptionCallback?: (option: IToolWithSelection) => void
  ) => {
    if (!options?.length) {
      return [
        <div className="noResultsContainer" key="noResults">
          <BordIlustrationIcon variant="loupe" standardSize={48} />
          <div className="noResultsMessageContainer">
            <span className="noResultsMessage">
              No hay resultados
            </span>
            <span className="noResultsSubMessage">
              Intenta nuevamente o crea una nueva herramienta en el módulo de catálogo
            </span>
          </div>
          <BordButton
            type="button"
            label="Ir al catálogo"
            onClick={handleGoToCatalogue}
            customFontSize="1.4rem"
            customHeight="auto"
            buttonIconTwo='externalLink'
          />
        </div>
      ];
    }

    return options.map(option => {
      const formattedPills =
        typeof option?.pills === 'string' ? (JSON.parse(option?.pills) as IpillsByReference) : option?.pills || {};

      const isSelected = option.isSelected;

      const handleContainerClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onToolToggleRef.current(option);
        clickOptionCallback?.(option);
      };

      const handleCheckboxClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onToolToggleRef.current(option);
      };

      return (
        <div key={option?.id} className="toolOptionContainer" onClick={handleContainerClick}>
          <input type="checkbox" className="toolCheckbox" checked={isSelected} onClick={handleCheckboxClick} readOnly />
          <NudosHoverText text={option?.name} charactersLimit={35} onlyIfTruncated maxHoverWidth="300px" />
          <div className="toolOptionPillsContainer">
            <NudosProductPills pills={formattedPills} characterLimit={6} />
          </div>
          <p className="toolOptionSku">SKU: {option?.sku}</p>
        </div>
      );
    });
  };

  const formSelectedOptionUI = (option: IToolWithSelection) => (
    <div className="selectedOptionContainer">
      <span>{option?.name}</span>
    </div>
  );

  const filterBySearchString = (search: string, options: IToolWithSelection[]) => {
    onSearchQueryChange(search);
    return options;
  };

  const handleGoToMetabaseSku = () => {
    window.open(META_BASE_STOCK_ORDER_URL, '_blank');
  };

  const handleGoToCatalogue = () => {
    window.open(`${window.location.origin}/catalog/product-list`, '_blank');
  };

  useEffect(() => {
    onToolToggleRef.current = onToolToggle;
  }, [onToolToggle]);

  return (
    <div className="toolsSearchDropdown">
      <NudosSearchbarDropdown
        label=""
        componentSize="extraLarge"
        formSelectedOptionUICallback={formSelectedOptionUI}
        formDropdownOptionsCallback={formDropdownOptions}
        filterBySearchStringCallback={filterBySearchString}
        customPlaceholder="Busca por código de SKU o modelo"
        currentValue={currentValue}
        isFilled={!!searchQuery}
        rawOptions={toolsWithSelection}
        closesOnChangeSelection={false}
        cleanSearchString={false}
      />
      <div className="searchSkusContainer">
        <span className="searchSkusText">
          Usa Metabase para buscar SKUs, pills y otros filtros de forma más rápida
        </span>
        <div className="searchSkusTextLinkContainer">
          <span className="searchSkusTextLink" onClick={handleGoToMetabaseSku}>
            Ir a Metabase
          </span>
          <BordOneToneIcon variant="externalLink" standardSize={12} stroke={bordColorTheme.cyan[400]} />
        </div>
      </div>
    </div>
  );
};

export default ToolsSearchDropdown;
